/**
 * Automation Media Hook
 *
 * Provides utilities for managing automation delivery method (media) configuration.
 * Handles email, SMS, push notification, and in-app message options.
 *
 * Usage:
 * const { media, mediaOptions, hasEmail, hasSms, mediaLabel, mediaList } =
 *   useAutomationMedia(automation);
 */

import { useSession } from 'src/contexts/SessionContext';

import { AutomationItem } from '../types';

// Media types match v2: email, text (SMS), in-app (push), message
export type MediaOption = 'email' | 'text' | 'in-app' | 'message';

interface MediaResult {
  /** Currently selected media types */
  media: string[];
  /** All available media options for selection (filtered by automation type) */
  mediaOptions: Array<{ value: MediaOption; label: string }>;
  /** Whether any media is selected */
  hasMedia: boolean;
  /** Whether email is selected */
  hasEmail: boolean;
  /** Whether SMS is selected */
  hasSms: boolean;
  /** Whether push notification is selected */
  hasPush: boolean;
  /** Whether in-app message is selected */
  hasMessage: boolean;
  /** Human-readable media label (e.g., "Email", "3 delivery methods") */
  mediaLabel: string;
  /** Comma-separated list of human-readable media labels (e.g., "Email, SMS Text Message") */
  mediaList: string;
}

/** All available media options with labels (matches v2) */
const ALL_MEDIA_OPTIONS: Array<{ value: MediaOption; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'text', label: 'SMS Text Message' },
  { value: 'in-app', label: 'iOS/Android Push Notification' },
  { value: 'message', label: 'Platform Message' },
];

/**
 * Media options available per automation type (matches v2 exactly)
 * - message: All 4 options (Platform Message, Email, Push, SMS)
 * - custom: 3 options (Email, Push, SMS) - NO Platform Message
 * - workout-today, visits-left, upcoming-appointment: email, push, and SMS
 */
const MEDIA_OPTIONS_BY_TYPE: Record<string, MediaOption[]> = {
  message: ['message', 'email', 'in-app', 'text'],
  custom: ['email', 'in-app', 'text'],
  'workout-today': ['email', 'in-app'],
  'visits-left': ['email', 'in-app', 'text'],
  'upcoming-appointment': ['email', 'in-app', 'text'],
};

/** Default media options (email + push) */
const DEFAULT_MEDIA_OPTIONS: MediaOption[] = ['email', 'in-app'];

/**
 * Hook for managing automation media/delivery method options.
 *
 * @param automation - The automation item containing media configuration
 * @returns Media state and computed values
 */
export const useAutomationMedia = (automation: AutomationItem): MediaResult => {
  const { session } = useSession();
  const smsAvailable = session?.smsAvailable ?? false;

  const media = automation.media ?? ['email'];
  const customType = automation.customType || '';
  const reportType = automation.reportType;

  // Get media options for this automation type
  let allowedMediaTypes =
    MEDIA_OPTIONS_BY_TYPE[customType] || DEFAULT_MEDIA_OPTIONS;

  // Special case: 'custom' type with 'custom_business_metrics' report only allows email
  // This matches v2 behavior in client-reminder-custom.js
  // Note: reportType may come as string or number depending on API serialization
  if (
    customType === 'custom' &&
    String(reportType) === 'custom_business_metrics'
  ) {
    allowedMediaTypes = ['email'];
  }

  // Filter out SMS ('text') if platform doesn't have SMS available
  if (!smsAvailable) {
    allowedMediaTypes = allowedMediaTypes.filter((type) => type !== 'text');
  }

  // Filter ALL_MEDIA_OPTIONS to only include allowed types for this automation
  const mediaOptions = ALL_MEDIA_OPTIONS.filter((opt) =>
    allowedMediaTypes.includes(opt.value)
  );

  const hasMedia = media.length > 0;
  const hasEmail = media.includes('email');
  const hasSms = media.includes('text');
  const hasPush = media.includes('in-app');
  const hasMessage = media.includes('message');

  // Build comma-separated list of human-readable labels (matches v2 mediaList)
  const mediaList = media
    .map((m) => ALL_MEDIA_OPTIONS.find((opt) => opt.value === m)?.label || m)
    .join(', ');

  // Build human-readable media label (for single/multiple summary)
  let mediaLabel = 'No delivery method';
  if (media.length === 1) {
    const option = ALL_MEDIA_OPTIONS.find((opt) => opt.value === media[0]);
    mediaLabel = option?.label || media[0];
  } else if (media.length > 1) {
    mediaLabel = `${media.length} delivery methods`;
  }

  return {
    media,
    mediaOptions,
    hasMedia,
    hasEmail,
    hasSms,
    hasPush,
    hasMessage,
    mediaLabel,
    mediaList,
  };
};
